import { formatPlateNumber } from '../../utils/plateFormatter';
import React from 'react';
import StatusBadge from '../Status/StatusBadge';
import { phoneDigits, formatPhone } from '../../utils/phoneUtils';
import './datatable.css';

/*
 * Table cell types: every value of the same kind looks the same in every table.
 *
 *   text    CellText    regular text, one line, long values truncated with a tooltip
 *   id      CellId      contract / booking / reference numbers (mono, accent). No copy icon:
 *                       clicking a contract number or Emirates ID opens the quick-actions menu,
 *                       which already offers "Copy".
 *   plate   CellPlate   vehicle plate in a small outlined box
 *   number  CellNumber  counts and quantities (tabular figures, thousands separator)
 *   money   CellMoney   amounts with 2 decimals, optional currency
 *   date    CellDate    dd/mm/yyyy (and hh:mm when `time`)
 *   phone   CellPhone   phone numbers as plain text
 *   status  CellStatus  StatusBadge (dot + word)
 *   tag     CellTag     small pill for categories; Daily / Monthly / Invygo / Leasing … get
 *                       their own colour automatically (see CATEGORY_TONES)
 *   muted   CellMuted   a secondary line under a main value
 *
 * Meaningful colour: every cell accepts `tone` = 'danger' | 'warning' | 'success' | 'info' | 'accent'
 * (e.g. an expired date in red: <CellDate value={d} tone="danger" />).
 * All cell text is centred.
 */

const EMPTY = <span className="ds-cell-empty">—</span>;
const isEmpty = (v) => v === null || v === undefined || v === '';
const TONES = ['danger', 'warning', 'success', 'info', 'accent', 'neutral'];

export const toneClass = (tone) => (tone && TONES.includes(tone) ? `ds-cell--${tone}` : '');

/* Category colours: one meaning per colour across all tables. */
export const CATEGORY_TONES = {
    daily: 'brand',
    monthly: 'accent',
    invygo: 'info',
    leasing: 'success',
    lease: 'success',
    corporate: 'warning',
    weekly: 'info',
    airport: 'warning',
    delivery: 'neutral',
    yelo: 'accent',
};

export const categoryTone = (value) => {
    const key = String(value ?? '').trim().toLowerCase();
    if (!key) return 'neutral';
    if (CATEGORY_TONES[key]) return CATEGORY_TONES[key];
    const hit = Object.keys(CATEGORY_TONES).find((k) => key.includes(k));
    return hit ? CATEGORY_TONES[hit] : 'neutral';
};

export function CellText({ children, max = 260, title, tone }) {
    if (isEmpty(children)) return EMPTY;
    const t = title ?? (typeof children === 'string' ? children : undefined);
    return <span className={`ds-cell-text ${toneClass(tone)}`} style={{ maxWidth: max }} title={t}>{children}</span>;
}

export function CellId({ children, tone, quickKind }) {
    if (isEmpty(children)) return EMPTY;
    // data-quick-value lets the app-wide quick-actions menu (ContractQuickActions) open on any
    // click on the number: contract numbers and Emirates IDs get Copy / Ejar / Customer profile.
    // quickKind="identity" marks a Customer ID column: only there is a 10-digit number a Saudi id.
    const value = typeof children === 'string' || typeof children === 'number' ? String(children) : undefined;
    return <span className={`ds-cell-id ${toneClass(tone)}`} data-quick-value={value} data-quick-kind={quickKind}>{children}</span>;
}

// A plate always reads "W 61268": letters, a space, the number (sources send "W61268").
const plateText = (v) => (typeof v === 'string' ? formatPlateNumber(v) : v);   // the one plate format

export function CellPlate({ children, tone }) {
    if (isEmpty(children)) return EMPTY;
    return <span className={`ds-cell-plate ${tone ? `ds-cell-plate--${tone}` : ''}`}>{plateText(children)}</span>;
}

const NUM = new Intl.NumberFormat('en-US');
const MONEY = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toNumber = (v) => {
    if (typeof v === 'number') return v;
    const n = Number(String(v).replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : null;
};

export function CellNumber({ value, children, tone }) {
    const v = value ?? children;
    if (isEmpty(v)) return EMPTY;
    const n = toNumber(v);
    return <span className={`ds-cell-num ${toneClass(tone)}`}>{n === null ? v : NUM.format(n)}</span>;
}

export function CellMoney({ value, children, currency, tone }) {
    const v = value ?? children;
    if (isEmpty(v)) return EMPTY;
    const n = toNumber(v);
    return (
        <span className={`ds-cell-num ${toneClass(tone)}`}>
            {currency && <span className="ds-cell-currency">{currency} </span>}
            {n === null ? v : MONEY.format(n)}
        </span>
    );
}

const pad = (n) => String(n).padStart(2, '0');

/** Formats Date objects, ISO strings and timestamps as dd/mm/yyyy; other strings are shown as given. */
export function formatCellDate(value, withTime = false) {
    if (isEmpty(value)) return '';
    let d = null;
    if (value instanceof Date) d = value;
    else if (typeof value === 'number') d = new Date(value);
    else if (value && typeof value.toDate === 'function') d = value.toDate();
    else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) d = new Date(value);
    if (!d || Number.isNaN(d.getTime())) return String(value);
    const date = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    return withTime ? `${date} ${pad(d.getHours())}:${pad(d.getMinutes())}` : date;
}

export function CellDate({ value, children, time = false, tone }) {
    const v = value ?? children;
    if (isEmpty(v)) return EMPTY;
    return <span className={`ds-cell-date ${toneClass(tone)}`}>{formatCellDate(v, time)}</span>;
}

export function CellPhone({ children, tone }) {
    if (isEmpty(children)) return EMPTY;
    // one format with the country code ("+966540550044", owner 27 Sep 2026); text that is no phone stays as typed
    const shown = typeof children === 'string' || typeof children === 'number' ? (formatPhone(phoneDigits(children)) || children) : children;
    return <span className={`ds-cell-phone ${toneClass(tone)}`}>{shown}</span>;
}

export function CellStatus({ status, label, tone, title }) {
    if (isEmpty(status) && isEmpty(label)) return EMPTY;
    return <StatusBadge status={status} label={label} tone={tone} title={title} />;
}

/** Category pill. Colour comes from the value (Daily, Monthly, Invygo, Leasing …) unless `tone` is given. */
export function CellTag({ children, tone }) {
    if (isEmpty(children)) return EMPTY;
    const t = tone || categoryTone(typeof children === 'string' ? children : '');
    return <span className={`yelo-badge yelo-badge--${t} yelo-badge--sm`}>{children}</span>;
}

export function CellMuted({ children, tone }) {
    if (isEmpty(children)) return null;
    return <span className={`ds-cell-muted ${toneClass(tone)}`}>{children}</span>;
}

/** Render a raw value by type (used by DataTable when a column has `type` and no `cell`). */
export function renderCellByType(type, value, tone) {
    switch (type) {
        case 'id': return <CellId tone={tone}>{value}</CellId>;
        case 'plate': return <CellPlate tone={tone}>{value}</CellPlate>;
        case 'number': return <CellNumber value={value} tone={tone} />;
        case 'money': return <CellMoney value={value} tone={tone} />;
        case 'date': return <CellDate value={value} tone={tone} />;
        case 'datetime': return <CellDate value={value} time tone={tone} />;
        case 'phone': return <CellPhone tone={tone}>{value}</CellPhone>;
        case 'status': return <CellStatus status={value} tone={tone} />;
        case 'tag': return <CellTag tone={tone}>{value}</CellTag>;
        case 'text':
        default:
            return <CellText tone={tone}>{isEmpty(value) ? value : String(value)}</CellText>;
    }
}
